import { useCallback } from 'react';

import { TrackingCategory, TrackingType } from '@/entities/tracking/const';
import { TrackerEngine } from '@/entities/tracking/lib/tracker-engine';
import { IDictionary, TrackEventPayload } from '@/entities/tracking/lib/types';
import { PAGE_TYPES, TRACK_LINK_NAMES } from '@/utils/constants/tracking';

import { IAppLaunchContext } from './useTrackAppLaunch';

declare global {
    interface Window {
        kochava?: {
            appID: string;
            customTraits: string;
            disableAutoInstall: boolean;
            kvID: string;
            ko_click_id: string;
            campaign_id: string;
            logging: boolean;
            page_name: string;
            startTime: number;
            useCookie: boolean;
            utm: string;
            verbose: boolean;
        };
    }
}

const buildDeepLinkEvent = (
    context: IAppLaunchContext,
): { type: string; build: (pageDetail: IDictionary) => TrackEventPayload } => ({
    type: TRACK_LINK_NAMES.TRACK_DEEPLINK,
    build: (pageDetail: IDictionary) => {
        const kochava = context.kochavaClickId
            ? {
                  kochavaDeviceId: window.kochava?.kvID,
                  kochavaCampaignId:
                      context.kochavaCampaignId ||
                      context.utmCampaign ||
                      window.kochava?.campaign_id,
              }
            : {};

        return {
            type: TRACK_LINK_NAMES.TRACK_DEEPLINK,
            category: TrackingCategory.App.INTERACT,
            detail: {
                ...pageDetail,
                context: {
                    pageName: PAGE_TYPES.DEEPLINK,
                    ...(pageDetail.context as Record<string, unknown>),
                    ...context,
                    ...kochava,
                },
            },
        };
    },
});
/**
 * Returns a callback that registers a synthetic deeplink tracking event to fire
 * before the next PAGE_VIEW. No-ops if the launch context has no deepLinkSource.
 * @returns trackDeepLink — call with the app launch context inside a useEffect
 */
export const useTrackDeepLink = () => {
    return useCallback((context: Readonly<IAppLaunchContext>) => {
        if (!context.deepLinkSource) return;
        TrackerEngine.registerFireOnce(TrackingType.PAGE_VIEW, buildDeepLinkEvent(context));
    }, []);
};
